import { memo, useCallback, useMemo, useState } from "react";
import type { EChartsOption, SeriesOption } from "echarts";
import EChart from "./EChart";
import { defaultAxisLabel, defaultGrid, tooltipCss } from "./chartDefaults";
import type { EwmaSeries } from "../../metrics/ewma";
import type { AnomalyType } from "../../metrics/anomalies";
import type { AxisFormatter } from "./XmrChart";

const DATAZOOM_THRESHOLD = 35;

interface EwmaChartProps {
  series: EwmaSeries;
  axisFormatter?: AxisFormatter;
  axisName?: string;
}

function EwmaChart({ series, axisFormatter, axisName }: EwmaChartProps) {
  const n = series.timestamps.length;
  const dense = n > DATAZOOM_THRESHOLD;

  const defaultZoomStart = dense ? Math.max(0, ((n - 28) / n) * 100) : 0;
  const [zoomPct, setZoomPct] = useState<{ start: number; end: number }>({ start: defaultZoomStart, end: 100 });

  const [prevN, setPrevN] = useState(n);
  if (prevN !== n) {
    setPrevN(n);
    setZoomPct({ start: defaultZoomStart, end: 100 });
  }

  const option = useMemo<EChartsOption>(
    () => buildOption(series, zoomPct, axisFormatter, axisName),
    [series, zoomPct, axisFormatter, axisName],
  );

  const handleDataZoom = useCallback((params: unknown) => {
    const p = params as { batch?: Array<{ start?: number; end?: number }>; start?: number; end?: number };
    const start = p.batch?.[0]?.start ?? p.start;
    const end = p.batch?.[0]?.end ?? p.end;
    if (start == null || end == null) return;
    setZoomPct({ start, end });
  }, []);

  const onEvents = useMemo(() => (dense ? { datazoom: handleDataZoom } : undefined), [dense, handleDataZoom]);

  return <EChart option={option} height={400} onEvents={onEvents} />;
}

function buildOption(
  series: EwmaSeries,
  zoomPct: { start: number; end: number },
  axisFormatter?: AxisFormatter,
  axisName?: string,
): EChartsOption {
  const timestamps = series.timestamps.map(timestamp => timestamp.getTime());
  const n = timestamps.length;
  const dense = n > DATAZOOM_THRESHOLD;

  const visibleN = Math.round(((zoomPct.end - zoomPct.start) / 100) * n);
  const symbolSize = visibleN > 60 ? 0 : visibleN > DATAZOOM_THRESHOLD ? 4 : 6;

  const valueSeries: SeriesOption = {
    name: "EWMA",
    type: "line",
    data: timestamps.map((ts, idx) => [ts, series.values[idx]]),
    symbol: symbolSize > 0 ? "circle" : "none",
    symbolSize,
    lineStyle: { color: "#111" },
    itemStyle: { color: "#111" },
    smooth: true,
  };

  const centerSeries: SeriesOption = {
    name: "Target",
    type: "line",
    data: timestamps.map((ts, idx) => [ts, series.centerLine[idx]]),
    showSymbol: false,
    lineStyle: { color: "#8c8b7c", type: "dashed" },
  };

  const upperSeries: SeriesOption = {
    name: "UCL",
    type: "line",
    data: timestamps.map((ts, idx) => [ts, series.upperControl[idx]]),
    showSymbol: false,
    lineStyle: { color: "#bfbda7", type: "dashed" },
  };

  const lowerSeries: SeriesOption = {
    name: "LCL",
    type: "line",
    data: timestamps.map((ts, idx) => [ts, series.lowerControl[idx]]),
    showSymbol: false,
    lineStyle: { color: "#bfbda7", type: "dashed" },
  };

  const anomalySeries = buildAnomalySeries(series);

  return {
    animation: false,
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "cross" },
      extraCssText: tooltipCss,
      valueFormatter: axisFormatter ? value => axisFormatter(Number(value)) : undefined,
    },
    xAxis: {
      type: "time",
      boundaryGap: false,
    },
    yAxis: {
      type: "value",
      axisLabel: { formatter: axisFormatter ?? defaultAxisLabel, margin: 12 },
      name: axisName,
      nameLocation: "end",
      nameGap: 30,
    },
    grid: dense ? { ...defaultGrid, bottom: 70 } : defaultGrid,
    ...(dense
      ? {
          dataZoom: [
            {
              type: "slider",
              xAxisIndex: 0,
              filterMode: "none",
              start: zoomPct.start,
              end: zoomPct.end,
              height: 22,
              bottom: 10,
              borderColor: "transparent",
              backgroundColor: "rgba(29,29,31,0.04)",
              fillerColor: "rgba(29,29,31,0.08)",
              handleStyle: { color: "#8c8b7c", borderColor: "#8c8b7c" },
              dataBackground: { lineStyle: { color: "#bfbda7" }, areaStyle: { color: "rgba(191,189,167,0.15)" } },
              selectedDataBackground: {
                lineStyle: { color: "#8c8b7c" },
                areaStyle: { color: "rgba(140,139,124,0.15)" },
              },
              textStyle: { fontSize: 10, color: "#8c8b7c" },
            },
          ],
        }
      : {}),
    series: [valueSeries, centerSeries, upperSeries, lowerSeries, ...anomalySeries],
  } satisfies EChartsOption;
}

export default memo(EwmaChart);

function buildAnomalySeries(series: EwmaSeries): SeriesOption[] {
  const anomalyColor: Record<AnomalyType, string> = {
    outside_limits: "#ff0000",
    two_sigma: "#e17655",
    run_of_eight: "#927cff",
  };
  const anomalySymbol: Record<AnomalyType, string> = {
    outside_limits: "circle",
    two_sigma: "diamond",
    run_of_eight: "triangle",
  };
  const anomalyName: Record<AnomalyType, string> = {
    outside_limits: "Rule 1: Outside Limits",
    two_sigma: "Rule 2: 2 of 3 ≥2σ",
    run_of_eight: "Rule 3: Run of 8",
  };

  const buckets: Record<AnomalyType, Array<[number, number]>> = {
    outside_limits: [],
    two_sigma: [],
    run_of_eight: [],
  };

  const timeToIndex = new Map<number, number>();
  for (let i = 0; i < series.timestamps.length; i++) {
    timeToIndex.set(series.timestamps[i].getTime(), i);
  }

  for (const anomaly of series.anomalies) {
    const startIdx = timeToIndex.get(anomaly.start.getTime());
    const endIdx = timeToIndex.get(anomaly.end.getTime());
    if (startIdx === undefined || endIdx === undefined) continue;
    for (let idx = startIdx; idx <= endIdx; idx++) {
      buckets[anomaly.type].push([series.timestamps[idx].getTime(), series.values[idx]]);
    }
  }

  return (Object.keys(buckets) as AnomalyType[])
    .filter(type => buckets[type].length > 0)
    .map(type => ({
      name: anomalyName[type],
      type: "scatter",
      data: buckets[type],
      symbol: anomalySymbol[type],
      symbolSize: 12,
      itemStyle: {
        color: "#ffffff",
        borderColor: anomalyColor[type],
        borderWidth: 3,
      },
      emphasis: { focus: "series" },
      tooltip: {
        valueFormatter: value => (typeof value === "number" ? value.toFixed(2) : value),
      },
    }));
}
